import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Star, Truck, ShieldCheck, RotateCcw, Heart, Minus, Plus, MapPin, Check, Award, AlertCircle, Clock, BadgeCheck, ShoppingCart } from 'lucide-react';
import { CatalogAPI, BuyerAPI, formatPrice, formatRelative, freshnessColor } from '../lib/api';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import ShareButtons from '../components/ShareButtons';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useJsonLd } from '../hooks/useSEO';

const ProductDetail = ({ onAddToCart }) => {
  const { id } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [offers, setOffers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [favorited, setFavorited] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [cartSubtotalBySupplier, setCartSubtotalBySupplier] = useState({});

  // Lit le panier (subtotal par fournisseur) pour afficher les hints de livraison gratuite
  useEffect(() => {
    const readCart = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('dz_cart') || '[]');
        const totals = {};
        for (const it of cart) {
          const sid = it?.variant?.supplier_id;
          if (!sid) continue;
          totals[sid] = (totals[sid] || 0) + (it.variant.price || 0) * (it.qty || 0);
        }
        setCartSubtotalBySupplier(totals);
      } catch { setCartSubtotalBySupplier({}); }
    };
    readCart();
    window.addEventListener('cart-changed', readCart);
    window.addEventListener('storage', readCart);
    return () => {
      window.removeEventListener('cart-changed', readCart);
      window.removeEventListener('storage', readCart);
    };
  }, []);

  useEffect(() => {
    setSelectedIdx(0);
    setQty(1);
    CatalogAPI.product(id).then(setProduct).catch(() => {});
    CatalogAPI.offers(id).then(setOffers).catch(() => setOffers([]));
    CatalogAPI.reviews(id).then(setReviews);
    if (user) {
      BuyerAPI.favorites().then((favs) => {
        setFavorited(favs.some((p) => p.id === id));
      }).catch(() => {});
    } else {
      setFavorited(false);
    }
  }, [id, user]);

  useEffect(() => {
    if (product?.category) {
      CatalogAPI.products({ category: product.category, limit: 4 }).then((items) =>
        setRelated(items.filter((p) => p.id !== product.id).slice(0, 4))
      );
    }
  }, [product]);

  // Dynamic SEO meta tags per product
  // BUG 4 fix: guard offers.length avant accès offers[0]
  const bestPrice = product && Array.isArray(offers) && offers.length > 0
    ? offers.reduce((m, o) => (o.in_stock && o.price < m ? o.price : m), offers[0].price)
    : null;
  useDocumentMeta({
    title: product ? `${product.name} ${product.brand ? '— ' + product.brand : ''} — Prix en Algérie | Comptoir Online` : 'Comptoir Online',
    description: product
      ? `${product.name} (${product.brand}) — Comparez ${offers.length || 'plusieurs'} offre(s) de fournisseurs dentaires en Algérie${bestPrice ? `. À partir de ${bestPrice} DA` : ''}. Paiement à la livraison.`
      : undefined,
    image: product?.image,
    type: 'product',
  });

  // Google Rich Results — Product structured data (JSON-LD)
  useJsonLd('product', product ? {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    image: product.image ? [product.image] : undefined,
    description: product.description || `${product.name} (${product.brand}) — Matériel dentaire professionnel en Algérie`,
    sku: product.id,
    brand: { '@type': 'Brand', name: product.brand },
    category: product.category,
    offers: offers.length > 0 ? {
      '@type': 'AggregateOffer',
      priceCurrency: 'DZD',
      lowPrice: Math.min(...offers.map(o => o.price)),
      highPrice: Math.max(...offers.map(o => o.price)),
      offerCount: offers.length,
      availability: offers.some(o => o.in_stock) ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    } : undefined,
    aggregateRating: product.rating_count > 0 ? {
      '@type': 'AggregateRating',
      ratingValue: product.rating_avg,
      reviewCount: product.rating_count,
    } : undefined,
  } : null);

  if (!product) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-[#7A6F5C]">Chargement...</div>;

  const inStockOffers = offers.filter((o) => o.in_stock);
  const bestOffer = inStockOffers[0] || offers[0];
  const selectedOffer = offers[selectedIdx] || bestOffer;

  const toggleFav = async () => {
    if (!user) { window.location.href = '/connexion'; return; }
    if (favorited) await BuyerAPI.removeFavorite(product.id);
    else await BuyerAPI.addFavorite(product.id);
    setFavorited(!favorited);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { window.location.href = '/connexion'; return; }
    try {
      await BuyerAPI.postReview({ product_id: product.id, rating: reviewForm.rating, comment: reviewForm.comment });
      toast({ title: 'Merci pour votre avis !' });
      setReviewForm({ rating: 5, comment: '' });
      CatalogAPI.reviews(id).then(setReviews);
      CatalogAPI.product(id).then(setProduct);
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Avis non envoyé' });
    }
  };

  const addToCartFromOffer = (offer, quantity = 1) => {
    if (!offer.in_stock) {
      toast({ title: 'Indisponible', description: 'Cette offre est en rupture de stock' });
      return;
    }
    const minQty = offer.min_qty || 1;
    if (quantity < minQty) {
      toast({ title: 'Quantité minimum', description: `Ce prix est valable à partir de ${minQty} unités. Veuillez ajuster la quantité.` });
      return;
    }
    onAddToCart && onAddToCart(product, {
      label: offer.label, price: offer.price, supplier_id: offer.supplier_id, stock: offer.stock, min_qty: minQty,
    }, quantity);
  };

  const savingsVsAverage = (price) => {
    if (offers.length < 2) return null;
    const avg = offers.reduce((s, o) => s + o.price, 0) / offers.length;
    if (price >= avg) return null;
    return Math.round(((avg - price) / avg) * 100);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-6">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to={`/categorie/${product.category}`} className="hover:text-[#1F4D3F] capitalize">{product.category}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26] truncate">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
          <div className="aspect-square bg-[#FAF7F2] relative">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=500&q=80'; }} />
            {product.discount && (
              <div className="absolute top-4 left-4 bg-[#C75B27] text-white text-sm font-semibold px-3 py-1.5 rounded">
                -{product.discount}%
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C75B27] font-medium mb-2">{product.brand}</div>
          <h1 className="font-serif text-3xl font-semibold text-[#1F4D3F] leading-tight">{product.name}</h1>

          <div className="mt-3 flex items-center gap-3">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map((s) => (
                <Star key={s} className={`w-4 h-4 ${s <= Math.round(product.rating || 0) ? 'fill-[#E0A52E] text-[#E0A52E]' : 'text-[#E8E0D0]'}`} />
              ))}
            </div>
            <span className="text-sm font-medium text-[#2D2A26]">{product.rating || 0}</span>
            <span className="text-sm text-[#7A6F5C]">({product.reviews || 0} avis)</span>
            <span className="text-sm text-[#7A6F5C]">·</span>
            <span className="text-sm font-medium text-[#1F4D3F]">{offers.length} offre{offers.length > 1 ? 's' : ''}</span>
          </div>

          <div className="mt-4">
            <ShareButtons
              url={`/produit/${product.id}`}
              text={`Découvrez ${product.name}${product.brand ? ' (' + product.brand + ')' : ''} sur Comptoir Online —`}
            />
          </div>

          {bestOffer && (
            <div className="mt-6 p-5 bg-gradient-to-br from-[#F5F1EA] to-[#FAF7F2] rounded-xl border border-[#E8E0D0]">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#C75B27] font-semibold">
                <Award className="w-4 h-4" /> Meilleur prix disponible
              </div>
              <div className="font-serif text-4xl font-semibold text-[#1F4D3F] mt-1">{formatPrice(bestOffer.price)}</div>
              <div className="text-sm text-[#5A5247] mt-1">par {bestOffer.label?.toLowerCase()}</div>
              {bestOffer.supplier && (
                <div className="mt-3 flex items-center gap-2 text-sm text-[#5A5247]">
                  <MapPin className="w-4 h-4" /> {bestOffer.supplier.name} · {bestOffer.supplier.city} · ★ {bestOffer.supplier.rating}
                </div>
              )}
              {offers.length > 1 && (
                <div className="mt-2 text-xs text-[#5A5247]">
                  Le plus cher : <span className="font-medium">{formatPrice(Math.max(...offers.map(o => o.price)))}</span> · vous économisez jusqu'à <span className="font-semibold text-[#C75B27]">{formatPrice(Math.max(...offers.map(o => o.price)) - bestOffer.price)}</span>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-[#E8E0D0] rounded-lg bg-white">
              <button onClick={() => setQty(Math.max((selectedOffer && selectedOffer.min_qty) || 1, qty - 1))} className="w-10 h-11 flex items-center justify-center text-[#1F4D3F] hover:bg-[#FAF7F2]"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-medium">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-11 flex items-center justify-center text-[#1F4D3F] hover:bg-[#FAF7F2]"><Plus className="w-4 h-4" /></button>
            </div>
            <Button onClick={() => bestOffer && addToCartFromOffer(bestOffer, qty)} disabled={!bestOffer?.in_stock} className="flex-1 h-11 bg-[#1F4D3F] hover:bg-[#173B30] text-white">
              Ajouter au panier — meilleur prix
            </Button>
            <button onClick={toggleFav} className="w-11 h-11 rounded-lg border border-[#E8E0D0] flex items-center justify-center hover:bg-[#FAF7F2]">
              <Heart className={`w-5 h-5 ${favorited ? 'fill-[#C75B27] text-[#C75B27]' : 'text-[#7A6F5C]'}`} />
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="flex flex-col items-center text-center p-3 border border-[#E8E0D0] rounded-xl bg-white"><Truck className="w-5 h-5 text-[#1F4D3F] mb-1" /><div className="text-xs text-[#5A5247]">Livraison 48h</div></div>
            <div className="flex flex-col items-center text-center p-3 border border-[#E8E0D0] rounded-xl bg-white"><ShieldCheck className="w-5 h-5 text-[#1F4D3F] mb-1" /><div className="text-xs text-[#5A5247]">Garantie authentique</div></div>
            <div className="flex flex-col items-center text-center p-3 border border-[#E8E0D0] rounded-xl bg-white"><RotateCcw className="w-5 h-5 text-[#1F4D3F] mb-1" /><div className="text-xs text-[#5A5247]">Paiement à la livraison</div></div>
          </div>
        </div>
      </div>

      {/* Offers comparison table */}
      <div className="mt-12">
        <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-[#1F4D3F]">Comparer les offres ({offers.length})</h2>
            <p className="text-sm text-[#7A6F5C] mt-1">Triées du moins cher au plus cher · indisponibles en bas de liste</p>
          </div>
          {offers.length > 1 && (
            <div className="text-xs flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> &lt; 24h</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500"></span> &lt; 7j</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-rose-500"></span> &gt; 7j</span>
            </div>
          )}
        </div>
        <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Fournisseur</th>
                <th className="px-4 py-3">Conditionnement</th>
                <th className="px-4 py-3">Localisation</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Mise à jour</th>
                <th className="px-4 py-3 text-right">Prix</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o, idx) => {
                const isBest = o.in_stock && bestOffer && o.price === bestOffer.price && o.supplier_id === bestOffer.supplier_id && o.label === bestOffer.label;
                const savings = savingsVsAverage(o.price);
                const supSubtotal = cartSubtotalBySupplier[o.supplier_id] || 0;
                const inCart = supSubtotal > 0;
                const threshold = o.supplier?.free_shipping_threshold;
                const hasFreeShipping = typeof threshold === 'number' && threshold > 0;
                const projected = supSubtotal + (o.price || 0);
                const freeReached = hasFreeShipping && supSubtotal >= threshold;
                const freeUnlocks = hasFreeShipping && !freeReached && projected >= threshold;
                const remaining = hasFreeShipping && projected < threshold ? threshold - projected : 0;
                return (
                  <tr key={`${o.supplier_id || 'unknown'}-${o.label}`} className={`border-t border-[#F5F1EA] ${!o.in_stock ? 'opacity-60' : 'hover:bg-[#FAF7F2]'} ${isBest ? 'bg-emerald-50/40' : ''} ${inCart && !isBest ? 'bg-[#F5F1EA]/40' : ''}`} data-testid={`offer-row-${idx}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {isBest && <Award className="w-4 h-4 text-[#C75B27]" />}
                        <div>
                          <div className="font-medium text-[#2D2A26] flex items-center gap-1 flex-wrap">
                            {o.supplier?.name || '—'}
                            {o.supplier?.approved && <BadgeCheck className="w-4 h-4 text-[#1F4D3F]" title="Fournisseur vérifié" />}
                            {inCart && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#1F4D3F] text-white px-1.5 py-0.5 rounded"
                                data-testid={`offer-in-cart-${idx}`}
                                title={`Déjà ${formatPrice(supSubtotal)} de produits ${o.supplier?.name} dans votre panier`}
                              >
                                <ShoppingCart className="w-2.5 h-2.5" /> Au panier
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[#7A6F5C]">★ {o.supplier?.rating || 0} · {o.supplier?.products_count || 0} produits</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-[#5A5247]">
                      {o.label}
                      {o.min_qty > 1 && (
                        <span className="ml-1 inline-block text-[10px] font-medium text-[#C75B27] bg-[#FFF3E0] px-1.5 py-0.5 rounded">
                          dès {o.min_qty}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-[#5A5247] whitespace-nowrap"><MapPin className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{o.supplier?.city || '—'}</td>
                    <td className="px-4 py-4">
                      {o.in_stock ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><Check className="w-3.5 h-3.5" /> Disponible</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-700"><AlertCircle className="w-3.5 h-3.5" /> Rupture</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${freshnessColor(o.freshness)}`}>
                        <Clock className="w-3 h-3" /> {formatRelative(o.last_updated)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="font-serif text-lg font-semibold text-[#1F4D3F]">{formatPrice(o.price)}</div>
                      {savings && <div className="text-xs text-[#C75B27] font-medium">−{savings}% vs moyenne</div>}
                      {inCart && o.in_stock && (
                        <div
                          className={`mt-1 text-[10px] font-medium inline-flex items-center gap-1 ${
                            freeReached || freeUnlocks ? 'text-emerald-700' : 'text-amber-800'
                          }`}
                          data-testid={`offer-shipping-hint-${idx}`}
                        >
                          <Truck className="w-3 h-3" />
                          {freeReached
                            ? 'Livraison gratuite ✓'
                            : freeUnlocks
                              ? 'Débloque livraison gratuite'
                              : remaining > 0
                                ? `+${formatPrice(remaining)} pour livraison gratuite`
                                : null}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button size="sm" disabled={!o.in_stock} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white disabled:bg-gray-300" onClick={() => addToCartFromOffer(o, 1)}>
                        {o.in_stock ? 'Commander' : 'Indisponible'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {offers.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#7A6F5C]">Aucune offre disponible pour ce produit.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-12">
        <h2 className="font-serif text-2xl font-semibold text-[#1F4D3F] mb-4">Avis clients ({reviews.length})</h2>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-3">
            {reviews.length === 0 ? (
              <div className="bg-white border border-[#E8E0D0] rounded-2xl p-8 text-center text-[#7A6F5C]">Soyez le premier à laisser un avis.</div>
            ) : reviews.map((r) => (
              <div key={r.id} className="bg-white border border-[#E8E0D0] rounded-xl p-4">
                <div className="flex items-center gap-2">
                  {[1,2,3,4,5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= r.rating ? 'fill-[#E0A52E] text-[#E0A52E]' : 'text-[#E8E0D0]'}`} />)}
                  <span className="text-sm font-medium text-[#2D2A26] ml-2">{r.user_name || 'Anonyme'}</span>
                  <span className="text-xs text-[#7A6F5C]">{new Date(r.created_at).toLocaleDateString('fr-DZ')}</span>
                </div>
                <p className="mt-2 text-sm text-[#5A5247]">{r.comment}</p>
              </div>
            ))}
          </div>
          <form onSubmit={submitReview} className="bg-white border border-[#E8E0D0] rounded-2xl p-5 h-fit">
            <h3 className="font-medium text-[#2D2A26] mb-3">Laisser un avis</h3>
            <div className="flex items-center gap-1 mb-3">
              {[1,2,3,4,5].map((s) => (
                <button key={s} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: s })}>
                  <Star className={`w-6 h-6 ${s <= reviewForm.rating ? 'fill-[#E0A52E] text-[#E0A52E]' : 'text-[#E8E0D0]'}`} />
                </button>
              ))}
            </div>
            <textarea required value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} placeholder="Votre expérience avec ce produit..." rows={4} className="w-full border border-[#E8E0D0] rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]" />
            <Button type="submit" className="w-full mt-3 bg-[#1F4D3F] hover:bg-[#173B30] text-white">Publier</Button>
          </form>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <h2 className="font-serif text-2xl font-semibold text-[#1F4D3F] mb-4">Produits similaires</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((p) => <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
